/**
 * useSoapMerge — SOAP editable local sincronizado con el documento único.
 *
 * Rediseño NU: la protección REAL de las ediciones del médico vive en el
 * servidor (guardas por sección en notaActual.ts). Este hook:
 *   - Mantiene el estado local editable (respuesta inmediata al tecleo).
 *   - PERSISTE cada edición vía `portal_editarNota` con debounce (~1.5 s),
 *     marcando la sección como edición humana server-side.
 *   - Aplica las actualizaciones que llegan del documento (aiSoap) a las
 *     secciones que el médico no tiene "sucias" localmente.
 *   - Expone las PROPUESTAS de la IA sobre secciones protegidas (vienen del
 *     documento: propuestasIA) para aceptar/descartar — la resolución también
 *     va al servidor.
 *
 * Mantiene el highlight visual de secciones recién actualizadas por la IA.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { SOAPData } from '../services/medicalAI';

type SoapKey = keyof SOAPData;

const EMPTY_SOAP: SOAPData = { s: '', o: '', a: '', p: '', e: '' };

/** How long (ms) a section stays highlighted after an AI update. */
const HIGHLIGHT_DURATION_MS = 3500;
/** Debounce (ms) antes de persistir una edición del médico. */
const PERSIST_DEBOUNCE_MS = 1500;

export interface SoapRemoteOptions {
  /** Propuestas de la IA sobre secciones protegidas (doc.propuestasIA). */
  propuestas?: Partial<SOAPData>;
  /** Persiste un parche de secciones editadas por el médico (CF editarNota). */
  persistir?: (patch: Partial<SOAPData>) => void;
  /** Resuelve una propuesta de la IA server-side (aceptar o descartar). */
  resolverPropuesta?: (key: SoapKey, aceptar: boolean) => void;
}

export interface UseSoapMergeReturn {
  /** Current editable SOAP (local state). */
  localSoap: SOAPData;
  /** Call when the doctor types in a SOAP textarea. Marks the section as edited. */
  handleSoapChange: (key: SoapKey, value: string) => void;
  /** Apply a COMPLETE SOAPE (e.g. from the medical assistant). */
  applySoap: (soap: Partial<SOAPData>) => void;
  /** AI-proposed updates for sections the doctor has already edited. */
  pendingAIUpdates: Partial<SOAPData>;
  /** Accept an AI proposal: replaces the section and clears the pending entry. */
  acceptAIUpdate: (key: SoapKey) => void;
  /** Dismiss an AI proposal without applying it. */
  dismissAIUpdate: (key: SoapKey) => void;
  /** Whether there is at least one pending AI update. */
  hasPendingUpdates: boolean;
  /** Sections that were recently updated by AI (for highlight animation). */
  recentlyUpdated: Set<SoapKey>;
  /** Secciones que el médico editó a mano (para marcarlas en la nota). */
  doctorEdited: Set<SoapKey>;
}

export function useSoapMerge(
  aiSoap: SOAPData,
  soapTabVisible = true,
  remote?: SoapRemoteOptions,
): UseSoapMergeReturn {
  const [localSoap, setLocalSoap] = useState<SOAPData>(EMPTY_SOAP);
  const [recentlyUpdated, setRecentlyUpdated] = useState<Set<SoapKey>>(new Set());
  // Propuestas locales acumuladas (transición: además de las del servidor).
  const [pendingLocal, setPendingLocal] = useState<Partial<SOAPData>>({});

  // Secciones que el médico editó en esta sesión (autoritativas localmente).
  // Se mantiene en ref (lectura síncrona dentro de efectos) y en estado (para
  // que la nota pueda marcarlas visualmente como "editado por ti").
  const doctorEditedRef = useRef<Set<SoapKey>>(new Set());
  const [doctorEdited, setDoctorEdited] = useState<Set<SoapKey>>(new Set());
  const marcarEditada = useCallback((key: SoapKey) => {
    if (doctorEditedRef.current.has(key)) return;
    doctorEditedRef.current.add(key);
    setDoctorEdited((prev) => new Set(prev).add(key));
  }, []);
  // Secciones con tecleo aún NO persistido (el snapshot no debe pisarlas).
  const dirtyRef = useRef<Set<SoapKey>>(new Set());
  // Parche pendiente de persistir (acumulado durante el debounce).
  const patchRef = useRef<Partial<SOAPData>>({});
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const remoteRef = useRef(remote);
  const visibleRef = useRef(soapTabVisible);

  useEffect(() => {
    remoteRef.current = remote;
    visibleRef.current = soapTabVisible;
  }, [remote, soapTabVisible]);

  const lastAISoapRef = useRef<SOAPData>(EMPTY_SOAP);
  const highlightTimersRef = useRef<Partial<Record<SoapKey, ReturnType<typeof setTimeout>>>>({});
  const unseenRef = useRef<Set<SoapKey>>(new Set());

  /** Envía a la CF lo acumulado en patchRef y limpia el estado sucio. */
  const flushPatch = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = undefined;
    }
    const patch = patchRef.current;
    if (Object.keys(patch).length === 0) return;
    patchRef.current = {};
    dirtyRef.current.clear();
    remoteRef.current?.persistir?.(patch);
  }, []);

  // Al desmontar (colgar/navegar) persistir lo que quedara en el debounce.
  useEffect(() => flushPatch, [flushPatch]);

  /** Apply the highlight animation to a list of sections with auto-fade timers. */
  function startHighlight(keys: SoapKey[]) {
    setRecentlyUpdated((prev) => {
      const next = new Set(prev);
      for (const key of keys) next.add(key);
      return next;
    });
    for (const key of keys) {
      if (highlightTimersRef.current[key]) clearTimeout(highlightTimersRef.current[key]);
      highlightTimersRef.current[key] = setTimeout(() => {
        setRecentlyUpdated((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
        delete highlightTimersRef.current[key];
      }, HIGHLIGHT_DURATION_MS);
    }
  }

  // ── Handle incoming AI/server updates ──
  useEffect(() => {
    const prev = lastAISoapRef.current;
    const changed = (Object.keys(EMPTY_SOAP) as SoapKey[]).some(
      (k) => aiSoap[k] !== prev[k],
    );
    if (!changed) return;

    lastAISoapRef.current = aiSoap;

    const sectionsApplied: SoapKey[] = [];

    setLocalSoap((current) => {
      const next = { ...current };
      for (const key of Object.keys(EMPTY_SOAP) as SoapKey[]) {
        const aiValue = aiSoap[key];
        if (!aiValue) continue; // server sent empty — keep whatever we have
        // Sección con tecleo sin persistir → no pisar lo que escribe el médico.
        if (dirtyRef.current.has(key)) continue;
        if (aiValue !== current[key]) {
          next[key] = aiValue;
          // Solo resaltar si el cambio no es el eco de una edición propia.
          if (!doctorEditedRef.current.has(key)) sectionsApplied.push(key);
        }
      }
      return next;
    });

    if (sectionsApplied.length > 0) {
      if (visibleRef.current) {
        startHighlight(sectionsApplied);
      } else {
        for (const key of sectionsApplied) unseenRef.current.add(key);
      }
    }
  }, [aiSoap.s, aiSoap.o, aiSoap.a, aiSoap.p, aiSoap.e]); // eslint-disable-line react-hooks/exhaustive-deps

  // When the SOAP tab becomes visible, flash any unseen updates
  useEffect(() => {
    if (soapTabVisible && unseenRef.current.size > 0) {
      const keys = Array.from(unseenRef.current);
      unseenRef.current.clear();
      startHighlight(keys);
    }
  }, [soapTabVisible]); // eslint-disable-line react-hooks/exhaustive-deps



  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      for (const timer of Object.values(highlightTimersRef.current)) {
        if (timer) clearTimeout(timer);
      }
    };
  }, []);

  // ── Doctor types in a textarea ──
  const handleSoapChange = useCallback((key: SoapKey, value: string) => {
    marcarEditada(key);
    dirtyRef.current.add(key);
    patchRef.current[key] = value;
    setLocalSoap((prev) => ({ ...prev, [key]: value }));
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(flushPatch, PERSIST_DEBOUNCE_MS);
  }, [flushPatch, marcarEditada]);

  // ── Accept a pending AI update ──
  const acceptAIUpdate = useCallback((key: SoapKey) => {
    const propuestaServidor = remoteRef.current?.propuestas?.[key];
    if (propuestaServidor !== undefined) {
      // Optimista: aplicar localmente; el servidor mueve propuesta → soap.
      setLocalSoap((soap) => ({ ...soap, [key]: propuestaServidor }));
      marcarEditada(key);
      remoteRef.current?.resolverPropuesta?.(key, true);
    } else {
      setPendingLocal((prev) => {
        const value = prev[key];
        if (value !== undefined) {
          setLocalSoap((soap) => ({ ...soap, [key]: value }));
          remoteRef.current?.persistir?.({ [key]: value });
        }
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }

    setRecentlyUpdated((prev) => new Set(prev).add(key));
    if (highlightTimersRef.current[key]) clearTimeout(highlightTimersRef.current[key]);
    highlightTimersRef.current[key] = setTimeout(() => {
      setRecentlyUpdated((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }, HIGHLIGHT_DURATION_MS);
  }, []);

  // ── Aplicar un SOAPE COMPLETO (p. ej. del asistente médico) ──
  // El asistente YA escribió server-side (portal_asistenteMedico → documento
  // único); esto aplica el cambio de inmediato en la UI sin esperar el
  // snapshot, y NO re-persiste (el valor entrante coincidirá con el eco).
  const applySoap = useCallback((soap: Partial<SOAPData>) => {
    const changedKeys = new Set<SoapKey>();
    setLocalSoap((current) => {
      const next = { ...current };
      for (const key of Object.keys(EMPTY_SOAP) as SoapKey[]) {
        const v = soap[key];
        if (typeof v !== 'string') continue;
        if (v === current[key]) continue;
        next[key] = v;
        marcarEditada(key);
        changedKeys.add(key);
      }
      return next;
    });
    if (changedKeys.size > 0) {
      const changed = Array.from(changedKeys);
      setPendingLocal((prev) => {
        const n = { ...prev };
        for (const k of changed) delete n[k];
        return n;
      });
      startHighlight(changed);
    }
  }, []);

  // ── Dismiss a pending AI update ──
  const dismissAIUpdate = useCallback((key: SoapKey) => {
    if (remoteRef.current?.propuestas?.[key] !== undefined) {
      remoteRef.current?.resolverPropuesta?.(key, false);
    }
    setPendingLocal((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  // Vista combinada: propuestas del servidor (autoritativas) + locales.
  const pendingAIUpdates: Partial<SOAPData> = {
    ...pendingLocal,
    ...(remote?.propuestas ?? {}),
  };

  const hasPendingUpdates = (Object.keys(EMPTY_SOAP) as SoapKey[])
    .some((k) => pendingAIUpdates[k] !== undefined);

  return {
    localSoap,
    handleSoapChange,
    applySoap,
    pendingAIUpdates,
    acceptAIUpdate,
    dismissAIUpdate,
    hasPendingUpdates,
    recentlyUpdated,
    doctorEdited,
  };
}
